import { useEffect, useRef } from 'react'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { useTranslation } from 'react-i18next'
import { sanitizeHtml } from 'helpers/sanitizeHtml'
import { useNotification } from 'hooks/notifications/useNotification'
import { Wrapper } from './styled'

interface IDownloadedImage {
  url: string
  data: string
}

interface IProps {
  content: string
  backgroundImage: File | string | null
  onGenerate: (pdf: jsPDF | null) => void
  preview?: boolean
}

const GeneratePdf = ({
  content,
  backgroundImage,
  onGenerate,
  preview = false,
}: IProps) => {
  const { t } = useTranslation()
  const notification = useNotification()
  const element = useRef<HTMLDivElement>(null)

  const generate = async (html: HTMLDivElement) => {
    try {
      const pdf = new jsPDF('landscape', 'px', [1100, 852], true)
      if (preview) pdf.setProperties({ title: 'Preview' })
      const width = pdf.internal.pageSize.getWidth()
      const height = pdf.internal.pageSize.getHeight()

      if (backgroundImage) {
        // A freshly picked File is never cached
        const cacheable = typeof backgroundImage === 'string'
        let url = ''
        if (cacheable) {
          url = backgroundImage
        } else {
          url = URL.createObjectURL(backgroundImage)
        }

        const baseUrl = url.split('?')[0]
        // The cache is optional: a corrupt entry or a blocked or full storage
        // never stops the PDF
        let cached: IDownloadedImage | undefined
        try {
          if (cacheable) {
            const imageCache: IDownloadedImage[] = JSON.parse(
              localStorage.getItem('bgImageCache') || '[]'
            )
            cached = imageCache.find(
              (item) =>
                item.url === baseUrl && item.data?.startsWith('data:image/')
            )
          }
        } catch {
          // An unreadable cache is a cache miss
        }
        let bgImage: string | void

        if (cached) {
          bgImage = cached.data
        } else {
          bgImage = await fetch(url, {
            headers: {
              'Cache-Control': 'no-cache',
            },
          })
            .then((res) => {
              if (!res.ok) {
                throw new Error(
                  `Background image request failed: ${res.status}`
                )
              }
              return res.blob()
            })
            .then((blob) => {
              // A successful answer that is not an image never reaches addImage
              if (!blob.type.startsWith('image/')) {
                throw new Error(`Background image has type "${blob.type}"`)
              }
              return new Promise<string>((resolve) => {
                const reader = new FileReader()
                reader.onloadend = () => resolve(reader.result as string)
                reader.readAsDataURL(blob)
              })
            })
            .catch(() => {
              notification.warning(
                t('reusables.generatePdf.notifications.errorLoadingBgImage')
              )
            })
          if (bgImage && cacheable) {
            // A single entry keeps the storage bounded and replaces any
            // leftovers from older versions of this cache
            try {
              localStorage.setItem(
                'bgImageCache',
                JSON.stringify([{ url: baseUrl, data: bgImage }])
              )
            } catch {
              // When the new entry does not fit, the previous cache still
              // holds the storage: drop it. A blocked storage skips the cache.
              try {
                localStorage.removeItem('bgImageCache')
              } catch {
                // Nothing to free
              }
            }
          }
        }
        if (bgImage) pdf.addImage(bgImage, 'JPEG', 0, 0, width, height)
      }

      const canvas = await html2canvas(html, {
        logging: true,
        imageTimeout: 1000,
        scale: 1.2,
        allowTaint: true,
        useCORS: true,
        backgroundColor: null,
      })
      const imgData = canvas.toDataURL('img/png')
      pdf.addImage(imgData, 'PNG', 0, 0, width, height)

      onGenerate(pdf)
    } catch (e) {
      console.error(e)
      notification.error(
        t('reusables.generatePdf.notifications.errorGenerating')
      )
      onGenerate(null)
    }
  }

  useEffect(() => {
    const target = element.current
    if (!target) return

    const timeout = setTimeout(() => {
      if (target.innerHTML.trim()) {
        generate(target)
      } else {
        notification.error(
          t('reusables.generatePdf.notifications.errorGenerating')
        )
        onGenerate(null)
      }
    }, 0)

    return () => clearTimeout(timeout)
  }, [content])

  return (
    <Wrapper>
      <div
        ref={element}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
      />
    </Wrapper>
  )
}

export default GeneratePdf
